/**
 * Checklists page
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { listChecklistRuns, createChecklistRun } from '../services/checklists';
import { listInstallations } from '../services/installations';
import { listTemplates } from '../services/checklists';
import { useAuth } from '../contexts/AuthContext';
import type { ChecklistRun, ChecklistTemplate, Installation } from '../../../shared/src/types';
import '../styles/Checklists.css';

export default function Checklists() {
  const [checklistRuns, setChecklistRuns] = useState<ChecklistRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [installations, setInstallations] = useState<Installation[]>([]);
  const [templates, setTemplates] = useState<ChecklistTemplate[]>([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  const canCreate = user?.role === 'Admin' || user?.role === 'Supervisor' || user?.role === 'Técnico';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [runs, insts, tmpls] = await Promise.all([
        listChecklistRuns(),
        listInstallations({ status: 'active' }),
        listTemplates(),
      ]);
      setChecklistRuns(runs);
      setInstallations(insts);
      setTemplates(tmpls);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    try {
      setCreating(true);
      setError('');

      const data = {
        installation_id: formData.get('installation_id') as string,
        template_id: formData.get('template_id') as string,
      };

      const run = await createChecklistRun(data);
      setShowCreateForm(false);
      navigate(`/checklists/${run.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error creating checklist');
    } finally {
      setCreating(false);
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pendiente';
      case 'in_progress':
        return 'En progreso';
      case 'blocked':
        return 'Bloqueado';
      case 'completed':
        return 'Completado';
      case 'closed':
        return 'Cerrado';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'status-pending';
      case 'in_progress':
        return 'status-in-progress';
      case 'blocked':
        return 'status-blocked';
      case 'completed':
        return 'status-completed';
      case 'closed':
        return 'status-closed';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="checklists">
        <div className="page-header">
          <h1>Checklists</h1>
        </div>
        <div className="loading">Cargando checklists...</div>
      </div>
    );
  }

  return (
    <div className="checklists">
      <div className="page-header">
        <h1>Checklists</h1>
        {canCreate && (
          <button
            className="btn-primary"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            {showCreateForm ? 'Cancelar' : 'Nuevo checklist'}
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {showCreateForm && canCreate && (
        <div className="create-form-card">
          <h2>Crear nuevo checklist</h2>
          <form onSubmit={handleCreate}>
            <div className="form-field">
              <label htmlFor="installation_id">Instalación *</label>
              <select id="installation_id" name="installation_id" required>
                <option value="">Seleccionar instalación</option>
                {installations.map((inst) => (
                  <option key={inst.id} value={inst.id}>
                    {inst.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label htmlFor="template_id">Plantilla *</label>
              <select id="template_id" name="template_id" required>
                <option value="">Seleccionar plantilla</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={creating}>
                {creating ? 'Creando...' : 'Crear checklist'}
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowCreateForm(false)}
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {checklistRuns.length === 0 ? (
        <div className="empty-state">
          <p>No hay checklists registrados</p>
          {canCreate && (
            <button className="btn-primary" onClick={() => setShowCreateForm(true)}>
              Crear checklist
            </button>
          )}
        </div>
      ) : (
        <div className="checklists-list">
          {checklistRuns.map((run) => (
            <div key={run.id} className="checklist-card">
              <div className="card-header">
                <h3>Checklist #{run.id.substring(0, 8)}</h3>
                <span className={`status-badge ${getStatusColor(run.status)}`}>
                  {getStatusLabel(run.status)}
                </span>
              </div>
              <div className="card-body">
                <div className="card-info">
                  <span className="info-label">Instalación:</span>
                  <span className="info-value">{run.installation_id.substring(0, 8)}...</span>
                </div>
                <div className="card-info">
                  <span className="info-label">Creado:</span>
                  <span className="info-value">
                    {new Date(run.created_at).toLocaleDateString('es-ES')}
                  </span>
                </div>
              </div>
              <div className="card-actions">
                <button
                  className="btn-secondary"
                  onClick={() => navigate(`/checklists/${run.id}`)}
                >
                  Ver detalle
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
